import type {
  ScheduleFromTakeoffItem,
  ScheduleFromTakeoffPreview,
} from '@obraplan/core/application/contracts/schedule-from-takeoff';
import { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { formatBusinessDate } from '@/lib/format';
import { barOn, formatDecimal, type Timeline } from './schedule-items-model';
import {
  groupPreviewByFloor,
  hoursText,
  plural,
  previewTimeline,
  quantityText,
  signed,
} from './takeoff-preview-model';

/**
 * A proposta do "Gerar do quantitativo" (Fase 31, RN-415 a RN-418): o resumo, os avisos e os
 * itens pavimento a pavimento, cada um com a barra na mesma régua.
 *
 * Só mostra: o que entra e o que fica de fora é do servidor.
 */
export function TakeoffPreview({ preview }: { readonly preview: ScheduleFromTakeoffPreview }) {
  const groups = useMemo(() => groupPreviewByFloor(preview.items), [preview.items]);
  const timeline = useMemo(() => previewTimeline(preview.items), [preview.items]);
  const segmented = preview.items.filter((item) => item.segmentedItems > 0).length;
  const { summary } = preview;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <p className="text-sm" data-testid="resumo-da-proposta">
        <strong>{plural(summary.items, 'item', 'itens')}</strong>:{' '}
        {plural(summary.create, 'novo', 'novos')} e {summary.existing}{' '}
        {summary.existing === 1 ? 'que já existe' : 'que já existem'}
        {preview.endDate !== null && (
          <>
            {' '}
            · de {formatBusinessDate(preview.startDate)} a{' '}
            <strong>{formatBusinessDate(preview.endDate)}</strong>
          </>
        )}
      </p>

      <Warnings preview={preview} segmented={segmented} />

      {groups.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">
          Nenhum pavimento × serviço com número: nada a gerar.
        </p>
      ) : (
        <TableWrapper className="max-h-[50vh] overflow-y-auto rounded-md border border-(--color-border-subtle)">
          <Table className="min-w-[56rem] [&_td]:px-2 [&_th]:px-2">
            <TableHeader sticky>
              <TableRow className="hover:bg-transparent">
                <TableHead>Serviço</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead className="text-right">HH</TableHead>
                <TableHead className="text-right">Equipe</TableHead>
                <TableHead className="text-right">Dias úteis</TableHead>
                <TableHead>Início → fim</TableHead>
                <TableHead className="min-w-[12rem]">
                  <span className="sr-only">Barra no tempo</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            {groups.map((group) => (
              <TableBody key={group.floorId} aria-label={`${group.towerName} · ${group.floorName}`}>
                <TableRow className="bg-(--color-surface-muted)/50 hover:bg-(--color-surface-muted)/50">
                  <TableCell colSpan={7} className="py-1.5 text-sm font-medium">
                    {group.towerName} · {group.floorName}
                    <span className="ml-2 text-xs font-normal text-(--color-content-muted)">
                      {plural(group.items.length, 'item', 'itens')}
                    </span>
                  </TableCell>
                </TableRow>
                {group.items.map((item) => (
                  <PreviewRow
                    key={`${item.floorId}|${item.serviceId}`}
                    item={item}
                    timeline={timeline}
                  />
                ))}
              </TableBody>
            ))}
          </Table>
        </TableWrapper>
      )}
    </div>
  );
}

function PreviewRow({
  item,
  timeline,
}: {
  readonly item: ScheduleFromTakeoffItem;
  readonly timeline: Timeline | undefined;
}) {
  const bar = timeline === undefined ? undefined : barOn(timeline, item.start, item.end);
  const showFileQuantity =
    item.fileQuantity !== null && item.quantityDifference !== null && item.quantityDifference !== 0;
  const showFileHours =
    item.fileLaborHours !== null &&
    item.laborHoursDifference !== null &&
    item.laborHoursDifference !== 0;

  return (
    <TableRow data-testid="item-da-proposta">
      <TableCell>
        <div className="flex flex-col gap-1">
          <span>
            <span className="text-(--color-content-muted)">{item.disciplineName} · </span>
            {item.serviceName}
          </span>
          <span className="flex flex-wrap gap-1">
            {item.existing && (
              <Badge
                tone="info"
                className="whitespace-nowrap"
                title="O item já existe: as datas dele ficam, e a quantidade e o HH passam a ser os do quantitativo"
              >
                já existe
              </Badge>
            )}
            {item.withoutProductivity && (
              <Badge
                tone="warn"
                className="whitespace-nowrap"
                title="O serviço não tem item de mão de obra em hora: dura 1 dia útil no pavimento"
              >
                sem produtividade
              </Badge>
            )}
            {item.segmentedItems > 0 && (
              <Badge
                tone="warn"
                className="whitespace-nowrap"
                title="Já há itens com trecho neste pavimento × serviço: o número pode estar dobrando"
              >
                {plural(item.segmentedItems, 'trecho', 'trechos')} ao lado
              </Badge>
            )}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right tabular whitespace-nowrap">
        {quantityText(item.quantity, item.unit)}
        {showFileQuantity && (
          <span
            className="block text-xs text-(--color-content-muted)"
            title="A diferença do quantitativo para o número do arquivo, que fica guardado"
          >
            arquivo: {quantityText(item.fileQuantity, item.unit)} (
            {signed(item.quantityDifference, 3)})
          </span>
        )}
      </TableCell>
      <TableCell className="text-right tabular whitespace-nowrap">
        {item.laborHours === null ? '—' : formatDecimal(item.laborHours)}
        {showFileHours && (
          <span className="block text-xs text-(--color-content-muted)">
            arquivo: {hoursText(item.fileLaborHours)} ({signed(item.laborHoursDifference)})
          </span>
        )}
      </TableCell>
      <TableCell className="text-right tabular">{item.crew}</TableCell>
      <TableCell className="text-right tabular">{item.workdays}</TableCell>
      <TableCell className="tabular whitespace-nowrap">
        {formatBusinessDate(item.start)} → {formatBusinessDate(item.end)}
      </TableCell>
      <TableCell className="min-w-[12rem]">
        {bar !== undefined && (
          <div className="relative h-3 rounded-sm bg-(--color-surface-muted)" aria-hidden="true">
            <div
              className={
                item.existing
                  ? 'absolute inset-y-0 rounded-sm bg-(--color-content-muted)/45'
                  : 'absolute inset-y-0 rounded-sm bg-(--color-brand)/70'
              }
              style={{ left: `${String(bar.left)}%`, width: `${String(bar.width)}%` }}
            />
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Os avisos da proposta: o que fica de fora, o que dura um dia e o que pode dobrar. */
function Warnings({
  preview,
  segmented,
}: {
  readonly preview: ScheduleFromTakeoffPreview;
  readonly segmented: number;
}) {
  const warnings: { key: string; title: string; body: string }[] = [];
  if (preview.withoutProductivity.length > 0) {
    warnings.push({
      key: 'sem-produtividade',
      title: `Sem produtividade (${String(preview.withoutProductivity.length)})`,
      body: `${preview.withoutProductivity
        .map(
          (service) =>
            `${service.disciplineName} · ${service.serviceName} (${plural(service.floors, 'pavimento', 'pavimentos')})`,
        )
        .join(
          '; ',
        )}. Sem item de mão de obra em hora, cada um dura 1 dia útil por pavimento — lance a produtividade no quantitativo para a duração sair do HH.`,
    });
  }
  if (preview.unknownServices.length > 0) {
    warnings.push({
      key: 'servicos-desconhecidos',
      title: `Serviços que a obra não tem (${String(preview.unknownServices.length)})`,
      body: `${preview.unknownServices
        .map((service) => `${service.disciplineName} · ${service.serviceName}`)
        .join('; ')}. Ficaram de fora do cronograma.`,
    });
  }
  if (preview.unplacedUnitTypes.length > 0) {
    warnings.push({
      key: 'tipologias-sem-pavimento',
      title: `Tipologias sem pavimento (${String(preview.unplacedUnitTypes.length)})`,
      body: `${preview.unplacedUnitTypes
        .map((unitType) => unitType.name)
        .join(
          '; ',
        )}. Têm número, mas nenhum pavimento posiciona o desenho delas — o número não entra. Ligue o pavimento ao desenho na Estrutura.`,
    });
  }
  if (segmented > 0) {
    warnings.push({
      key: 'trechos',
      title: `${plural(segmented, 'item', 'itens')} ao lado de trechos`,
      body: 'O pavimento × serviço já tem itens com trecho (a fachada por face). A geração cria o item sem trecho ao lado deles, e o número pode estar dobrando.',
    });
  }
  if (warnings.length === 0) return null;

  return (
    <ul className="flex flex-col gap-2" aria-label="Avisos da proposta">
      {warnings.map((warning) => (
        <li
          key={warning.key}
          data-aviso={warning.key}
          className="rounded-md border border-(--color-state-warn)/50 bg-(--color-state-warn)/10 px-3 py-2 text-sm"
        >
          <p className="font-medium">{warning.title}</p>
          <p className="text-xs text-(--color-content-muted)">{warning.body}</p>
        </li>
      ))}
    </ul>
  );
}
